import type { CardMixKey, PricingAssumptions } from '../api';

export const CARD_MIX_FIELDS: { key: CardMixKey; label: string }[] = [
	{ key: 'credit', label: 'Credit' },
	{ key: 'signature_debit', label: 'Signature debit' },
	{ key: 'pin_debit', label: 'PIN debit' },
	{ key: 'regulated_debit', label: 'Regulated debit' },
	{ key: 'amex', label: 'American Express' },
];

// Matches the API: the statement's mix is rounded to 0.01%, so its parts can miss 100 a little.
const MIX_SUM_TOLERANCE = 0.05;

/** The inputs as typed, so a half-typed number is not lost. */
export interface AssumptionsForm {
	monthly_volume: string;
	transaction_count: string;
	card_mix: Record<CardMixKey, string>;
}

export const toForm = (a: PricingAssumptions): AssumptionsForm => ({
	monthly_volume: String(a.monthly_volume),
	transaction_count: String(a.transaction_count),
	card_mix: Object.fromEntries(
		CARD_MIX_FIELDS.map(({ key }) => [key, String(a.card_mix[key] ?? 0)]),
	) as Record<CardMixKey, string>,
});

export const mixTotal = (form: AssumptionsForm): number =>
	CARD_MIX_FIELDS.reduce(
		(sum, { key }) => sum + (parseFloat(form.card_mix[key]) || 0),
		0,
	);

/** The payload for the API, or an error message a person can act on. */
export const parseForm = (
	form: AssumptionsForm,
): { value: PricingAssumptions } | { error: string } => {
	const volume = parseFloat(form.monthly_volume);
	const count = Number(form.transaction_count);
	if (!(volume > 0))
		return { error: 'The monthly volume must be more than zero.' };
	if (!Number.isInteger(count) || count < 1)
		return {
			error: 'There must be one or more transactions, as a whole number.',
		};
	const mix = {} as Record<CardMixKey, number>;
	for (const { key, label } of CARD_MIX_FIELDS) {
		const share = Number(form.card_mix[key] || 0);
		if (!(share >= 0))
			return { error: `${label} must be a number that is zero or more.` };
		mix[key] = share;
	}
	const total = mixTotal(form);
	if (Math.abs(total - 100) > MIX_SUM_TOLERANCE)
		return {
			error: `The card mix must add up to 100%. It adds up to ${Math.round(total * 100) / 100}% now.`,
		};
	return {
		value: { monthly_volume: volume, transaction_count: count, card_mix: mix },
	};
};

export const sameAssumptions = (
	a: PricingAssumptions,
	b: PricingAssumptions,
): boolean =>
	a.monthly_volume === b.monthly_volume &&
	a.transaction_count === b.transaction_count &&
	CARD_MIX_FIELDS.every(({ key }) => a.card_mix[key] === b.card_mix[key]);
